import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  CircleCheck,
  Diamond,
  Flame,
  HeartHandshake,
  Loader2,
  Lock,
  MessageCircle,
  Moon,
  PenLine,
  Shield,
  User,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { STATUS_SHAPES, type StatusKey } from "@/lib/status";
import { ApiError, getSponsees, type SponseeSummary } from "@/lib/api";
import { entryWhen, shortDate } from "@/lib/format";

const STATUS_LABEL: Record<StatusKey, string> = {
  green: "On Track",
  yellow: "Caution",
  red: "Needs Attention",
};

const DAY = 86_400_000;
const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / DAY);

/**
 * The one Step 4 notice a sponsee's status earns, if any (Step 4 canvas §11).
 * Status without content: the sponsor learns THAT the work is happening —
 * started, ready to be read aloud, gone quiet, or writing deleted — never a
 * word of it. Ready outranks everything (it's the actionable one); a recent
 * wipe outranks quiet/started; "quiet" is the 12-day anti-quit heads-up.
 */
function step4Notice(p: SponseeSummary): {
  kind: "s5done" | "s5waiting" | "ready" | "deleted" | "quiet" | "started";
  icon: typeof CircleCheck;
  emphasis: "primary" | "yellow" | "none";
  title: string;
  body: string;
  meta: string;
} | null {
  const s4 = p.step4;
  if (!s4) return null;
  // Step 5 (in person) outranks Step 4's ready state once it's relevant: the
  // sitting is the live thread between the two of them.
  if (p.step5 && daysSince(p.step5.worked_at) <= 14) {
    return {
      kind: "s5done",
      icon: CircleCheck,
      emphasis: "primary",
      title: `${p.name} marked Step 5 done`,
      body: "Read to you in person. Nothing about the conversation is stored in Happy24.",
      meta: `${daysSince(p.step5.worked_at) === 0 ? "Today" : shortDate(p.step5.worked_at)} · they marked it themselves`,
    };
  }
  // Step 4 ready for a long while and no sitting yet — the gentle nudge.
  if (!p.step5 && s4.status === "ready" && s4.ready_at && daysSince(s4.ready_at) >= 14) {
    return {
      kind: "s5waiting",
      icon: Moon,
      emphasis: "none",
      title: `Step 5 is still ahead for ${p.name}`,
      body: "Worth asking when a good hour is — not what's in it.",
      meta: `Ready since ${shortDate(s4.ready_at)}`,
    };
  }
  if (s4.status === "ready") {
    return {
      kind: "ready",
      icon: CircleCheck,
      emphasis: "primary",
      title: `${p.name} is ready to read their Step 4 to you`,
      body: "Nothing to read here — Step 5 happens in person, whenever you two can sit down.",
      meta: `Marked ready ${daysSince(s4.ready_at ?? s4.last_activity) === 0 ? "today" : shortDate(s4.ready_at ?? s4.last_activity)} · no writing shared`,
    };
  }
  if (s4.wiped_at && daysSince(s4.wiped_at) <= 14) {
    return {
      kind: "deleted",
      icon: Lock,
      emphasis: "none",
      title: `${p.name} deleted their Step 4 writing`,
      body: "Their choice, and the normal thing to do after Step 5. The step stays worked.",
      meta: `${daysSince(s4.wiped_at) === 0 ? "Today" : shortDate(s4.wiped_at)} · progress unchanged`,
    };
  }
  if (s4.status === "in_progress" && daysSince(s4.last_activity) >= 12) {
    return {
      kind: "quiet",
      icon: Diamond,
      emphasis: "yellow",
      title: `${p.name} hasn't worked Step 4 in ${daysSince(s4.last_activity)} days`,
      body: "A quiet heads-up, not a flag. Worth asking how it's going — not what's in it.",
      meta: `Last worked ${shortDate(s4.last_activity)}`,
    };
  }
  if (s4.status === "in_progress" && daysSince(s4.started_at) <= 7) {
    return {
      kind: "started",
      icon: PenLine,
      emphasis: "none",
      title: `${p.name} started Step 4`,
      body: "The long one. You'll see when they're working on it, never what they write.",
      meta: `Started ${shortDate(s4.started_at)}`,
    };
  }
  return null;
}

function Avatar({ name, status }: { name: string; status: StatusKey | null }) {
  const initial = (name.trim()[0] ?? "?").toUpperCase();
  const Shape = status ? STATUS_SHAPES[status] : null;
  return (
    <div className="relative shrink-0">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-[17px] font-semibold text-primary">
        {initial}
      </div>
      {status && Shape && (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-[15px] w-[15px] items-center justify-center rounded-full bg-card">
          <Shape
            className={`h-[10px] w-[10px] ${
              status === "green" ? "text-status-green" : status === "yellow" ? "text-status-yellow" : "text-status-red"
            }`}
            strokeWidth={2.5}
          />
        </span>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: StatusKey }) {
  const Shape = STATUS_SHAPES[status];
  return (
    <Badge status={status}>
      <Shape className="h-[11px] w-[11px]" strokeWidth={2} />
      {STATUS_LABEL[status]}
    </Badge>
  );
}

export default function SponsorDashboard() {
  const navigate = useNavigate();
  const [sponsees, setSponsees] = useState<SponseeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSponsees()
      .then(setSponsees)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else setError("Couldn't load your sponsees.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const flagged = sponsees.filter((s) => s.today_status === "red" || s.today_status === "yellow");
  const stepNotices = sponsees
    .map((p) => ({ p, n: step4Notice(p) }))
    .filter((x): x is { p: SponseeSummary; n: NonNullable<ReturnType<typeof step4Notice>> } => x.n !== null);

  return (
    <div className="pb-16">
      <div className="flex items-start justify-between gap-3 px-6 pb-4 pt-8">
        <div>
          <p className="text-sm text-muted-foreground">Sponsoring</p>
          <h1 className="text-2xl font-bold text-foreground">Your Sponsees</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/app")}>
          <User className="h-4 w-4" strokeWidth={2} />
          My Recovery
        </Button>
      </div>

      <div className="flex flex-col gap-3 px-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
            <span className="text-sm">Loading…</span>
          </div>
        ) : error ? (
          <p className="py-16 text-center text-sm text-muted-foreground">{error}</p>
        ) : sponsees.length === 0 ? (
          <Card>
            <CardContent className="px-4 py-6 text-center">
              <p className="text-sm font-medium text-foreground">No one shares with you yet</p>
              <p className="mt-1 text-[13px] text-muted-foreground">
                When someone adds you as their sponsor and accepts, they'll appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {(flagged.length > 0 || stepNotices.length > 0) && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Since you last looked
                </p>
                {flagged.map((p) => (
                  <Card
                    key={p.owner_id}
                    interactive
                    onClick={() => navigate(`/sponsor/${p.owner_id}`)}
                    className={p.today_status === "red" ? "border-2 border-status-red" : "border-2 border-status-yellow"}
                  >
                    <CardContent className="flex items-start gap-3 px-4 py-3">
                      <span
                        className={`mt-0.5 ${p.today_status === "red" ? "text-status-red-text" : "text-status-yellow-text"}`}
                      >
                        {p.today_status === "red" ? (
                          <HeartHandshake className="h-[18px] w-[18px]" strokeWidth={2} />
                        ) : (
                          <MessageCircle className="h-[18px] w-[18px]" strokeWidth={2} />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground">
                          {p.name} logged {STATUS_LABEL[p.today_status as StatusKey]} today
                        </p>
                        {p.today_note && (
                          <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
                            {p.today_note}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {stepNotices.map(({ p, n }) => {
                  const Icon = n.icon;
                  return (
                    <Card
                      key={`s4-${p.owner_id}`}
                      interactive
                      onClick={() =>
                        navigate(
                          `/sponsor/${p.owner_id}/steps/${n.kind === "s5done" || n.kind === "s5waiting" ? 5 : 4}`
                        )
                      }
                      className={
                        n.emphasis === "primary"
                          ? "border-2 border-primary"
                          : n.emphasis === "yellow"
                            ? "border-2 border-status-yellow"
                            : undefined
                      }
                    >
                      <CardContent className="flex items-start gap-3 px-4 py-3">
                        <span
                          className={`mt-0.5 ${n.emphasis === "yellow" ? "text-status-yellow-text" : "text-primary"}`}
                        >
                          <Icon className="h-[17px] w-[17px]" strokeWidth={2} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold leading-snug text-foreground">{n.title}</p>
                          <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{n.body}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{n.meta}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                <p className="px-1 text-xs text-muted-foreground">
                  A quiet heads-up — not an emergency alert. Reach out when you can.
                </p>
              </div>
            )}

            <p className="mt-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Connected · {sponsees.length}
            </p>
            {sponsees.map((p) => (
              <Card key={p.owner_id} interactive onClick={() => navigate(`/sponsor/${p.owner_id}`)}>
                <CardContent className="flex items-center gap-3 px-4 py-3.5">
                  <Avatar name={p.name} status={p.today_status} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold text-foreground">{p.name}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Flame className="h-3 w-3" strokeWidth={2} /> {p.streak}d
                    </p>
                    {p.last_check_in && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {entryWhen(p.last_check_in)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {p.today_status && <StatusBadge status={p.today_status} />}
                    <ChevronRight className="h-[18px] w-[18px] text-muted-foreground" strokeWidth={2} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        <Button variant="outline" fullWidth className="mt-1" onClick={() => navigate("/sharing")}>
          <UserPlus className="h-4 w-4" strokeWidth={2} />
          Manage Connections
        </Button>
        <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
          <Shield className="h-3 w-3" strokeWidth={2} />
          You see only what each person chooses to share. Their journal stays theirs.
        </p>
      </div>
    </div>
  );
}
